import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Plus, MoreVertical, Pencil, Trash2, Mail, Phone } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const Staff = () => {
  const { user, token } = useAuth();
  const [business, setBusiness] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    services: [],
    is_active: true,
    photo_url: ""
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const bizResponse = await axios.get(`${API}/businesses`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });

      if (bizResponse.data.length > 0) {
        const biz = bizResponse.data[0];
        setBusiness(biz);

        const [staffResponse, servicesResponse] = await Promise.all([
          axios.get(`${API}/businesses/${biz.business_id}/staff`, {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true
          }),
          axios.get(`${API}/businesses/${biz.business_id}/services`, {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true
          })
        ]);
        
        setStaffList(staffResponse.data);
        setServices(servicesResponse.data);
      }
    } catch (error) {
      toast.error("Errore nel caricamento dello staff");
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingStaff(null);
    setFormData({
      name: "",
      email: "",
      phone: "",
      services: [],
      is_active: true,
      photo_url: ""
    });
    setDialogOpen(true);
  };

  const openEditDialog = (staff) => {
    setEditingStaff(staff);
    setFormData({
      name: staff.name,
      email: staff.email,
      phone: staff.phone || "",
      services: staff.services || [],
      is_active: staff.is_active,
      photo_url: staff.photo_url || ""
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingStaff) {
        await axios.put(`${API}/staff/${editingStaff.staff_id}`, formData, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        toast.success("Staff aggiornato!");
      } else {
        await axios.post(`${API}/businesses/${business.business_id}/staff`, formData, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        });
        toast.success("Staff aggiunto!");
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (staffId) => {
    if (!confirm("Sei sicuro di voler eliminare questo membro dello staff?")) return;

    try {
      await axios.delete(`${API}/staff/${staffId}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      toast.success("Staff eliminato!");
      fetchData();
    } catch (error) {
      toast.error("Errore durante l'eliminazione");
    }
  };

  const toggleService = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.includes(serviceId)
        ? prev.services.filter(id => id !== serviceId)
        : [...prev.services, serviceId]
    }));
  };

  const getInitials = (name) => {
    return name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "S";
  };

  const getServiceName = (serviceId) => {
    return services.find(s => s.service_id === serviceId)?.name || serviceId;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!business) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-slate-500">Crea prima la tua attività dalla Dashboard</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="staff-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Staff
            </h1>
            <p className="text-slate-500 mt-1">Gestisci il personale della tua attività</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={openCreateDialog} data-testid="add-staff-btn">
                <Plus className="h-4 w-4 mr-2" />
                Nuovo Staff
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {editingStaff ? "Modifica Staff" : "Nuovo Staff"}
                </DialogTitle>
                <DialogDescription>
                  {editingStaff ? "Modifica i dettagli del membro dello staff" : "Aggiungi un nuovo membro al tuo team"}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nome *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Es. Mario Rossi"
                    required
                    data-testid="staff-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="mario@esempio.com"
                    required
                    data-testid="staff-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Telefono</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+39 123 456 7890"
                    data-testid="staff-phone-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="photo">URL Foto</Label>
                  <Input
                    id="photo"
                    value={formData.photo_url}
                    onChange={(e) => setFormData({ ...formData, photo_url: e.target.value })}
                    placeholder="https://..."
                    data-testid="staff-photo-input"
                  />
                </div>

                {services.length > 0 && (
                  <div className="space-y-2">
                    <Label>Servizi Offerti</Label>
                    <div className="space-y-2 max-h-40 overflow-y-auto border rounded-lg p-3">
                      {services.map((service) => (
                        <div key={service.service_id} className="flex items-center gap-2">
                          <Checkbox
                            id={`service-${service.service_id}`}
                            checked={formData.services.includes(service.service_id)}
                            onCheckedChange={() => toggleService(service.service_id)}
                            data-testid={`staff-service-${service.service_id}`}
                          />
                          <label htmlFor={`service-${service.service_id}`} className="text-sm cursor-pointer">
                            {service.name}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <Label htmlFor="active">Attivo</Label>
                  <Switch
                    id="active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    data-testid="staff-active-switch"
                  />
                </div>

                <DialogFooter>
                  <Button type="submit" disabled={saving} data-testid="save-staff-btn">
                    {saving ? "Salvataggio..." : editingStaff ? "Salva Modifiche" : "Aggiungi Staff"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Staff Grid */}
        {staffList.length === 0 ? (
          <Card className="border-dashed border-2 border-slate-200">
            <CardContent className="py-12 text-center">
              <p className="text-slate-500 mb-4">Nessun membro dello staff ancora aggiunto</p>
              <Button onClick={openCreateDialog} data-testid="empty-add-staff-btn">
                <Plus className="h-4 w-4 mr-2" />
                Aggiungi il primo membro
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger-children">
            {staffList.map((staff) => (
              <Card key={staff.staff_id} className="border-slate-100 shadow-sm card-hover" data-testid={`staff-card-${staff.staff_id}`}>
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-12 w-12">
                        <AvatarImage src={staff.photo_url} />
                        <AvatarFallback className="bg-primary/10 text-primary">
                          {getInitials(staff.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                          {staff.name}
                        </CardTitle>
                        {!staff.is_active && (
                          <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                            Disattivato
                          </span>
                        )}
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`staff-menu-${staff.staff_id}`}>
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditDialog(staff)}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Modifica
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-red-600"
                          onClick={() => handleDelete(staff.staff_id)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Elimina
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-slate-600">
                      <Mail className="h-4 w-4" />
                      <span className="truncate">{staff.email}</span>
                    </div>
                    {staff.phone && (
                      <div className="flex items-center gap-2 text-slate-600">
                        <Phone className="h-4 w-4" />
                        <span>{staff.phone}</span>
                      </div>
                    )}
                  </div>
                  {staff.services?.length > 0 && (
                    <div className="mt-4">
                      <p className="text-xs text-slate-500 mb-2">Servizi:</p>
                      <div className="flex flex-wrap gap-1">
                        {staff.services.slice(0, 3).map((serviceId) => (
                          <span key={serviceId} className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">
                            {getServiceName(serviceId)}
                          </span>
                        ))}
                        {staff.services.length > 3 && (
                          <span className="text-xs text-slate-500">+{staff.services.length - 3}</span>
                        )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Staff;
